import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { NAV_LINKS } from "@/data/content";

export default function Header({ onCtaClick }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("home");
  const location = useLocation();
  const mobileMenuRef = useRef(null);
  const menuButtonRef = useRef(null);
  const firstFocusableRef = useRef(null);
  const lastFocusableRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handler = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("keydown", handler);

    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) {
      return;
    }

    firstFocusableRef.current?.focus();

    const handleTab = (e) => {
      if (e.key !== "Tab") {
        return;
      }

      const first = firstFocusableRef.current;
      const last = lastFocusableRef.current;

      if (!first || !last) {
        return;
      }

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleTab);

    return () => document.removeEventListener("keydown", handleTab);
  }, [open]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);

      const sectionIds = [
        "home",
        "loans",
        "calculators",
        "why",
        "process",
        "testimonials",
        "contact",
      ];

      let current = "home";

      for (const id of sectionIds) {
        const section = document.getElementById(id);

        if (!section) continue;

        const top = section.offsetTop - 120;

        if (window.scrollY >= top) {
          current = id;
        }
      }

      setActiveSection(current);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event) => {
      const clickedInsideMenu = mobileMenuRef.current?.contains(event.target);

      const clickedMenuButton = menuButtonRef.current?.contains(event.target);

      if (!clickedInsideMenu && !clickedMenuButton) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const scrollToSection = (href) => {
    if (!href.startsWith("#")) return;

    const id = href.substring(1);
    const section = document.getElementById(id);

    if (!section) return;

    // Close the menu immediately
    setOpen(false);

    // Unlock scrolling immediately instead of waiting for the effect
    document.body.style.overflow = "";

    // Wait for the closing animation to finish
    setTimeout(() => {
      const y = section.getBoundingClientRect().top + window.scrollY;

      window.history.replaceState({}, "", href);

      window.scrollTo({
        top: y,
        behavior: "smooth",
      });
    }, 180);
  };

  const handleEligibilityClick = () => {
    if (location.pathname === "/") {
      onCtaClick?.();
      return;
    }

    navigate("/#eligibility");
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 mm-transition ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-gray-200/60"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between min-h-16 md:min-h-20">
          <Link
            to="/"
            data-testid="header-logo"
            className="flex items-center gap-2 group shrink-0"
          >
                <img
                  src="/Logo.webp"
                  alt="Money Mart"
                  className="h-[60px] w-auto object-contain"
                />
          </Link>

          <nav className="hidden lg:flex flex-1 min-w-0 flex-wrap items-center justify-center gap-x-8 gap-y-1 py-2">
            {NAV_LINKS.map((link) =>
              link.external ? (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`text-sm font-medium mm-transition whitespace-nowrap ${
                   link.href === "/"
                      ? location.pathname === "/"
                        ? "text-[#0F2D52] font-semibold border-b-2 border-[#D4AF37] pb-1"
                        : "text-gray-700 hover:text-[#0F2D52]"
                      : location.pathname === link.href ||
                          location.pathname.startsWith(`${link.href}/`)
                        ? "text-[#0F2D52] font-semibold border-b-2 border-[#D4AF37] pb-1"
                        : "text-gray-700 hover:text-[#0F2D52]"
                  }`}
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection(link.href);
                  }}
                  data-testid={`nav-link-${link.href.replace("#", "")}`}
                  className={`text-sm font-medium mm-transition whitespace-nowrap ${
                    activeSection === link.href.replace("#", "")
                      ? "text-[#0F2D52] font-semibold border-b-2 border-[#D4AF37] pb-1"
                      : "text-gray-700 hover:text-[#0F2D52]"
                  }`}
                >
                  {link.label}
                </a>
              ),
            )}
          </nav>

          <div className="flex items-center gap-2 shrink-0">
            <Button
              data-testid="header-cta-check-eligibility"
              onClick={handleEligibilityClick}
              className="hidden sm:inline-flex rounded-full bg-[#0F2D52] hover:bg-[#0a1f38] text-white px-5 h-10 text-sm font-semibold"
            >
              Check Eligibility
              <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
            <button
              ref={menuButtonRef}
              aria-expanded={open}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 mm-transition"
              onClick={() => setOpen((v) => !v)}
              aria-controls="mobile-navigation"
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-navigation"
            ref={mobileMenuRef}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-b border-gray-200"
          >
            <div className="px-4 sm:px-6 py-4 flex flex-col gap-1">
              {NAV_LINKS.map((link, index) =>
                link.external ? (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setOpen(false)}
                    ref={index === 0 ? firstFocusableRef : null}
                    data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`py-3 px-2 text-sm font-medium rounded-lg mm-transition ${
                      activeSection === link.href.replace("#", "")
                        ? "bg-blue-50 text-[#0F2D52] font-semibold"
                        : "text-gray-800 hover:bg-gray-50"
                    }`}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection(link.href);
                    }}
                    ref={index === 0 ? firstFocusableRef : null}
                    href={link.href}
                    data-testid={`mobile-nav-${link.href.replace("#", "")}`}
                    className={`py-3 px-2 text-sm font-medium rounded-lg mm-transition ${
                      activeSection === link.href.replace("#", "")
                        ? "bg-blue-50 text-[#0F2D52] font-semibold"
                        : "text-gray-800 hover:bg-gray-50"
                    }`}
                  >
                    {link.label}
                  </a>
                ),
              )}
              <Button
                data-testid="mobile-cta-check-eligibility"
                ref={lastFocusableRef}
                onClick={() => {
                  setOpen(false);
                  document.body.style.overflow = "";

                  setTimeout(() => {
                    handleEligibilityClick();
                  }, 180);
                }}
                className="mt-2 rounded-full bg-[#0F2D52] hover:bg-[#0a1f38] text-white h-11"
              >
                Check Eligibility
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
